import { useForm } from 'react-hook-form'
import { useCreatePendingAccount } from '../../hooks/admin/usePendingAccounts'
import type { PendingAccountCreateRequest, PendingAccountRole } from '../../types/admin'

const ROLE_OPTIONS: { value: PendingAccountRole; label: string; description: string }[] = [
  { value: 'ADMIN', label: 'Admin', description: 'Full system access' },
  { value: 'PARTNER', label: 'Partner', description: 'API access and device management' },
  { value: 'CUSTOMER', label: 'Customer', description: 'Standard user access' },
  { value: 'GUEST', label: 'Guest', description: 'Limited read-only access' },
]

interface UserInviteDialogProps {
  isOpen: boolean
  onClose: () => void
}

export default function UserInviteDialog({ isOpen, onClose }: UserInviteDialogProps) {
  const createInvite = useCreatePendingAccount()

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PendingAccountCreateRequest>({
    defaultValues: {
      email: '',
      role: 'CUSTOMER',
      firstName: '',
      lastName: '',
    },
  })

  const onSubmit = async (data: PendingAccountCreateRequest) => {
    try {
      await createInvite.mutateAsync(data)
      reset()
      onClose()
    } catch (error) {
      // Error handled by mutation
    }
  }

  const handleCancel = () => {
    reset()
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={handleCancel} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="w-full mt-3 text-center sm:mt-0 sm:text-left">
                <h3 className="text-2xl font-semibold leading-6 text-gray-900 mb-6">Invite New User</h3>

                <div className="space-y-4">
                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Email Address *</label>
                    <input
                      type="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Invalid email format',
                        },
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="user@example.com"
                    />
                    {errors.email && <p className="text-red-600 text-sm mt-1">{errors.email.message}</p>}
                  </div>

                  {/* Role Selection */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Role *</label>
                    <select
                      {...register('role', { required: 'Role is required' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {ROLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label} - {option.description}
                        </option>
                      ))}
                    </select>
                    {errors.role && <p className="text-red-600 text-sm mt-1">{errors.role.message}</p>}
                  </div>

                  {/* Optional First Name */}
                  <div>
                    <label className="block text-sm font-medium mb-2">First Name (optional)</label>
                    <input
                      type="text"
                      {...register('firstName')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="John"
                    />
                  </div>

                  {/* Optional Last Name */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Last Name (optional)</label>
                    <input
                      type="text"
                      {...register('lastName')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                {/* Info box */}
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-700">
                    An invitation email will be sent to the user with a registration link valid for 7 days.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
              <button
                type="submit"
                disabled={createInvite.isPending}
                className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createInvite.isPending ? 'Sending...' : 'Send Invitation'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={createInvite.isPending}
                className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
